import { KeyboardEvent, useEffect, useCallback, useState, useMemo, useContext, useRef } from 'react';
import { Box } from '@material-ui/core';
import { useForm } from 'react-hook-form';

import { SearchFilterSortUpload, Typography, UploadImageModal } from '@components';
import { content } from '@content';
import { imageLibrary, ModulesSearchParams } from '@modules';
import { useHistory } from '@utils';
import { useAppDispatch } from '@store';
import { TreatmentBuilderContext } from '@routes';
import { getConvertedImagesByWidth } from '@routes/ContentCenter/ImageLibrary/ImageLibrary.utils';

import { ImageLibraryPanelProps } from './ImageLibraryPanel.props';
import { useStyles } from './ImageLibraryPanel.styles';
import { UploadFormValues } from './ImageLibraryPanel.types';
import { DEFAULT_PANEL_IMAGE_WIDTH, INITIAL_QUERY, RUN_SEARCH_DELAY } from './ImageLibraryPanel.const';
import LoadingBlockPlaceholder from '@components/LoadingBlockPlaceholder/LoadingBlockPlaceholder';
import { SearchFormProps } from '@components/SearchFilterSortUpload/SearchFilterSortUpload.props';

import InfiniteScroll from 'react-infinite-scroll-component';

import useDebounceLast from '@utils/hooks/useDebounce';

/**
 ImageLibraryPanel component
 @returns {JSX.Element}
 */

export const ImageLibraryPanel = ({
  chosenImage,
  isImageLibrary = false,
  onChooseImage,
  searchKeyword = '',
  setSearchKeyword,
}: ImageLibraryPanelProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useAppDispatch();

  const { businessUnit } = useHistory().query;
  const { chosenModuleElemId } = useContext(TreatmentBuilderContext);

  const [chosenImageId, setChosenImageId] = useState('');
  const [showUploadImageModal, setShowUploadImageModal] = useState(false);

  const contentContainer = useRef<null | HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderRowCount, setLoaderRowCount] = useState(1);

  // Add a ref map for image elements
  const imageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const prevChosenImageSrc = useRef<string | undefined | null>(undefined);

  const { control, setValue, getValues } = useForm<SearchFormProps>({
    defaultValues: {
      searchType: 'library',
      keyword: searchKeyword,
      sortBy: 'name',
    },
  });

  const [query, setQuery] = useState<ModulesSearchParams>({
    ...INITIAL_QUERY,
    businessUnitId: businessUnit as number,
  });

  const libraryImages = imageLibrary.useAllData();
  const images = useMemo(() => {
    setIsLoading(false);
    setQuery((prevState) => ({
      ...prevState,
      page: libraryImages.currentPage,
    }));
    return getConvertedImagesByWidth(libraryImages.items, DEFAULT_PANEL_IMAGE_WIDTH);
  }, [libraryImages]);

  // useLoader(imageLibrary.useAllMeta(), imageLibrary.useEntityMeta());

  const getImages = useCallback(
    (keyword: string, page: number = query.page) => {
      if (businessUnit) {
        setIsLoading(true);

        return dispatch(imageLibrary.thunk.search({ ...query, keyword, businessUnit, page }));
      }
    },
    [businessUnit, dispatch, query],
  );

  // Note: this is resetting the image library list when the component is mounted
  // so it may need to be disable
  useEffect(() => {
    // Only run on mount
    // reset library and get images
    // dispatch(imageLibrary.actions.resetLibrary());
    // getImages('', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [debouncedSearch] = useDebounceLast(async (keyword: string, page: number) => {
    await getImages(keyword, page);
  }, RUN_SEARCH_DELAY);

  const handleKeywordChange = useCallback(
    (keyword: string) => {
      if (getValues().keyword !== keyword) {
        dispatch(imageLibrary.actions.resetLibrary());
        setValue('keyword', keyword);
        setQuery((prevState) => ({ ...prevState, keyword, page: 1 }));

        if (setSearchKeyword) {
          setSearchKeyword(keyword);
        }

        // runSearch(keyword, 1);
        debouncedSearch(keyword, 1).catch(() => {
          /* Handle error if needed */
        });
      }
    },
    [getValues, dispatch, setValue, setSearchKeyword, debouncedSearch],
  );

  const handleSearchKeyUp = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleKeywordChange(getValues().keyword);
      }
    },
    [getValues, handleKeywordChange],
  );

  const loadMoreImages = () => {
    const nextPage = query.page + 1;

    if (nextPage <= libraryImages.pageCount) {
      setQuery((prevState) => {
        return { ...prevState, page: prevState.page + 1 };
      });

      getImages(getValues().keyword, nextPage);
    }
  };

  // populate the images on first load for content module editor view
  useEffect(() => {
    if (getValues().keyword === '' && images.length === 0 && !isLoading) {
      getImages('', 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUploadImageModalOpen = useCallback(() => {
    setShowUploadImageModal(true);
  }, [setShowUploadImageModal]);

  const handleUploadImageModalClose = useCallback(() => {
    setShowUploadImageModal(false);
  }, [setShowUploadImageModal]);

  const handleCreateImage = useCallback(
    async (file: File | null, businessUnitItem: number) => {
      const body = new FormData();

      body.append('file', file as Blob);

      const result = await dispatch(imageLibrary.thunk.create({ params: { businessUnit: businessUnitItem }, body }));

      if (result.payload && imageLibrary.thunk.create.fulfilled.match(result)) {
        handleUploadImageModalClose();
      }
    },
    [dispatch, handleUploadImageModalClose],
  );

  const handleImageUploadFormSubmit = useCallback(
    async (values: UploadFormValues) => {
      const promises = [];

      if (businessUnit && Array.isArray(values.files)) {
        for (const file of values.files) {
          promises.push(handleCreateImage(file, businessUnit));
        }
      } else if (businessUnit && !Array.isArray(values.files)) {
        promises.push(handleCreateImage(values.files, businessUnit));
      }

      await Promise.all(promises);
    },
    [handleCreateImage, businessUnit],
  );

  const handleChooseImage = useCallback(
    (item) => (e: any) => {
      const chosenElem = e.target;

      // get the image element that is being swapped and check for a parent anchor tag.
      // If there is alt text on the image, we will use it as the alt text for the chosen image.
      // If there is an anchor tag, we will use its href and title attributes.
      const targetIframe = document.getElementById('module-modal') as HTMLIFrameElement;
      const contentDocument = targetIframe?.contentDocument || targetIframe?.contentWindow?.document;
      const sourceImageElement = contentDocument?.getElementById(chosenModuleElemId) as HTMLImageElement;
      const sourceImageAnchor =
        sourceImageElement?.parentElement?.tagName === 'A' ? sourceImageElement.parentElement : null;

      const linkValue = sourceImageAnchor?.getAttribute('href') ?? sourceImageAnchor?.getAttribute('temphref') ?? '';
      const linkTitleValue = sourceImageAnchor?.getAttribute('title') ?? '';
      const altTextValue = sourceImageElement?.getAttribute('alt') ?? '';

      setChosenImageId(chosenElem.id);
      onChooseImage({
        id: item.id,
        chosenModuleElemId: chosenModuleElemId,
        // width: isImageLibrary ? item.width : Math.round(item.previewWidth * 0.5),
        width: item.width,
        // height: isImageLibrary ? item.height : Math.round(item.previewHeight * 0.5),
        height: item.height,
        imageSrc: item.path,
        name: item.name,
        link: linkValue,
        title: linkTitleValue,
        altText: altTextValue,
      });
    },
    [chosenModuleElemId, onChooseImage],
  );

  useEffect(() => {
    // Only scroll if chosenImage?.imageSrc has changed
    if (chosenImage?.imageSrc && prevChosenImageSrc.current !== chosenImage.imageSrc) {
      const curImage = images.find((image) => image.path === chosenImage.imageSrc);
      setChosenImageId((curImage?.id ?? '').toString());

      if (curImage && imageRefs.current[curImage.id]) {
        imageRefs.current[curImage.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    prevChosenImageSrc.current = chosenImage?.imageSrc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosenImage, images]);

  // useEffect(() => {
  //   const promise = getImages(searchKeyword ?? '');
  //   return () => promise?.abort();
  // }, [dispatch, getImages, searchKeyword]);

  useEffect(() => {
    if (contentContainer.current) {
      const containerHeight = contentContainer.current?.offsetHeight;
      const LOADER_HEIGHT_WITH_MARGIN = 255;
      setLoaderRowCount(() => Math.ceil(containerHeight / LOADER_HEIGHT_WITH_MARGIN));
    }
  }, [contentContainer]);

  return (
    <Box className={styles.imageLibraryPanel} {...{ ref: contentContainer }}>
      <Box className={styles.searchBar}>
        <SearchFilterSortUpload
          className={styles.sfsuContainer}
          control={control}
          onSearchTermChange={handleKeywordChange}
          onKeyUp={handleSearchKeyUp}
          onUploadClick={handleUploadImageModalOpen}
          placeholder={content.search}
          searchBarClassName={styles.sfsuSearchBar}
          showUpload={true}
          uploadButtonClassName={styles.sfsuUploadButton}
        />
      </Box>
      {showUploadImageModal && (
        <UploadImageModal onSubmit={handleImageUploadFormSubmit} onClose={handleUploadImageModalClose} />
      )}

      <Box className={styles.content} id="image-library-scrollable-div">
        <InfiniteScroll
          next={loadMoreImages}
          loader=""
          hasMore={libraryImages.currentPage < libraryImages.pageCount}
          dataLength={libraryImages.items.length}
          scrollableTarget="image-library-scrollable-div"
        >
          {isLoading &&
            images.length === 0 &&
            [...Array(loaderRowCount)].map((_: number, index: number) => (
              <Box key={`loaderRow-${index}`} className={styles.loaderBlock}>
                <LoadingBlockPlaceholder />
              </Box>
            ))}
          {images.map((item) => (
            <div
              key={`${item.name}-${item.id}`}
              data-chosen={chosenImageId === String(item.id)}
              className={styles.imageWrapper}
              ref={(el) => (imageRefs.current[item.id] = el)}
              onClick={handleChooseImage(item)}
            >
              <div id={String(item.id)} className={styles.image} style={{ backgroundImage: `url(${item.path})` }} />
              <Box className={styles.imageContent}>
                <Box className={styles.moduleRow}>
                  <Typography.Headline className={styles.name}>{item.name}</Typography.Headline>
                </Box>
                <Box className={styles.moduleRow}>
                  <Typography.SmallCaption className={styles.index}>
                    {`${Math.round(item.width)}x${Math.round(item.height)}`}
                  </Typography.SmallCaption>
                </Box>
              </Box>
            </div>
          ))}
        </InfiniteScroll>
      </Box>
    </Box>
  );
};
